// src/pages/Groups/CreateGroup.jsx
import React, { useState } from "react";
import { FaSave, FaTimes, FaInfoCircle } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { createGroup } from "../../services/groupServices";
import { toast } from "react-toastify";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const colors = [
  { label: "Bleu", value: "bg-blue-500 text-white" },
  { label: "Vert", value: "bg-green-500 text-white" },
  { label: "Jaune", value: "bg-yellow-400 text-slate-900" },
  { label: "Rouge", value: "bg-red-500 text-white" },
  { label: "Indigo", value: "bg-indigo-500 text-white" },
  { label: "Violet", value: "bg-purple-500 text-white" },
  { label: "Rose", value: "bg-pink-500 text-white" },
  { label: "Orange", value: "bg-orange-500 text-white" },
  { label: "Cyan", value: "bg-teal-500 text-white" },
];

const DEFAULT_FORM = {
  nom: "",
  ordre_groupe: "",
  is_active: true,
  color_groupe: colors[0].value,
  photo_url: "",
  description: "",
};

const CreateGroup = ({ isActive, onClose, refreshGroups }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleColorChange = (selectedOption) => {
    if (!selectedOption) return;
    setFormData((prev) => ({
      ...prev,
      color_groupe: selectedOption.value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        // Si ordre_groupe est vide → null (la BDD peut gérer automatiquement)
        ordre_groupe:
          formData.ordre_groupe === ""
            ? null
            : Number(formData.ordre_groupe) || 0,
      };

      await createGroup(payload);
      toast.success("Groupe créé avec succès");
      resetForm();
      onClose();
      refreshGroups();
    } catch (error) {
      console.error("Erreur lors de la création du groupe", error);
      toast.error("Erreur lors de la création du groupe");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive) return null;

  const selectedColorOption =
    colors.find((c) => c.value === formData.color_groupe) || colors[0];

  const firstLetter = (formData.nom || "?").charAt(0).toUpperCase();

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-2">
      <div className="modal-box bg-white dark:bg-slate-900 p-0 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 text-white">
          <h3 className="text-lg sm:text-xl font-bold uppercase tracking-wide text-center">
            Créer un nouveau groupe
          </h3>
          <p className="mt-2 text-xs sm:text-sm flex items-center justify-center gap-2 opacity-90">
            <FaInfoCircle className="hidden sm:inline-block" />
            Définissez un groupe (Commission, Pôle, Fonction…) pour organiser
            vos acteurs du judo.
          </p>
        </div>

        {/* Contenu formulaire */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Nom + couleur + aperçu */}
          <div className="grid grid-cols-1 md:grid-cols-[2fr,1.3fr] gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Nom du groupe *
              </label>
              <input
                type="text"
                className="input input-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                placeholder="Ex : Commission Arbitrage, Bureau, Pôle Communication…"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Couleur du groupe
              </label>
              <Select
                name="color_groupe"
                value={selectedColorOption}
                onChange={handleColorChange}
                options={colors}
                className="text-sm"
                classNamePrefix="react-select"
                getOptionLabel={(option) => (
                  <div className="flex items-center">
                    <span
                      className={`w-4 h-4 inline-block rounded-full mr-2 ${option.value}`}
                    />
                    {option.label}
                  </div>
                )}
                getOptionValue={(option) => option.value}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "36px",
                    borderRadius: "0.75rem",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />

              {/* Petit aperçu visuel */}
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <span
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${formData.color_groupe}`}
                >
                  {firstLetter}
                </span>
                <span>Aperçu du badge du groupe</span>
              </div>
            </div>
          </div>

          {/* Image + ordre + statut */}
          <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr,1fr] gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-700 dark:text-slate-200">
                URL de l’image (optionnel)
              </label>
              <input
                type="url"
                className="input input-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                name="photo_url"
                value={formData.photo_url}
                onChange={handleChange}
                placeholder="https://… (bandeau ou logo du groupe)"
              />
              {formData.photo_url && (
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
                  Cette image sera utilisée sur les pages où le groupe apparaît.
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Ordre d’affichage
              </label>
              <input
                type="number"
                className="input input-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                name="ordre_groupe"
                value={formData.ordre_groupe}
                onChange={handleChange}
                placeholder="Optionnel"
                min="0"
              />
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
                Laissez vide pour laisser l’application gérer l’ordre.
              </p>
            </div>

            <div className="flex flex-col justify-center">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Statut
              </label>
              <label className="inline-flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-700 dark:text-slate-200">
                  Groupe actif
                </span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
              </label>
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
                Un groupe inactif reste en base mais n’est plus proposé dans les
                sélecteurs.
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Description (optionnelle)
              </label>
              <span className="text-[11px] text-slate-400">
                Utilisée dans certaines pages publiques ou internes.
              </span>
            </div>
            <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800">
              <ReactQuill
                value={formData.description}
                onChange={handleContentChange}
                className="quill-sm"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-3 border-t border-slate-200 dark:border-slate-700 mt-2">
            <Button
              text={isSubmitting ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={isSubmitting}
              className="btn-primary gap-2 !rounded-full px-5"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="btn-ghost gap-2 !rounded-full px-5"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroup;
